import { useEffect, useState } from 'react'
import { type CrossClientQueryResult } from '../../../models/CrossClient'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery } from '../../../store/crossClient/crossClient'
import { CcqCards, MATCHED_CARD_FILTER, cardForResult } from '../Results/Run/ccqResultView'
import { collectResultsByIds } from '../Results/Run/useCcqCardResults'
import { type SendBookingInvitationsTarget } from './sendBookingInvitationsRoute'

const uniqueClientIds = (results: CrossClientQueryResult[]): string[] => Array.from(new Set(results.map((result) => result.clientId)))

const resultIdsByClientId = (results: CrossClientQueryResult[]): Record<string, string> =>
  results.reduce<Record<string, string>>((byClient, result) => {
    if (!(result.clientId in byClient)) {
      byClient[result.clientId] = result.id
    }
    return byClient
  }, {})

const EMPTY_RESULT_IDS: Record<string, string> = {}

interface ClientIdsLookup {
  target: SendBookingInvitationsTarget
  clientIds: string[] | null
  resultIdByClientId: Record<string, string>
  failed: boolean
}

export interface SendBookingInvitationsClientIds {
  clientIds: string[] | null
  resultIdByClientId: Record<string, string>
  isError: boolean
}

export const useSendBookingInvitationsClientIds = (executionId: string, target: SendBookingInvitationsTarget): SendBookingInvitationsClientIds => {
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetAll] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const [lookup, setLookup] = useState<ClientIdsLookup | null>(null)

  useEffect(() => {
    if (target.kind === 'clients') {
      return
    }
    let ignore = false
    const resolve = async (): Promise<CrossClientQueryResult[]> => {
      if (target.kind === 'results') {
        const results = await collectResultsByIds(triggerGetAll, { accountId, executionId }, target.resultIds)
        return results.filter((result) => cardForResult(result) === CcqCards.MATCHED)
      }
      const response = await triggerGetAll({ accountId, executionId, ...MATCHED_CARD_FILTER }).unwrap()
      return response?.data ?? []
    }
    resolve()
      .then((results) => {
        if (!ignore) {
          setLookup({ target, clientIds: uniqueClientIds(results), resultIdByClientId: resultIdsByClientId(results), failed: false })
        }
      })
      .catch(() => {
        if (!ignore) {
          setLookup({ target, clientIds: null, resultIdByClientId: EMPTY_RESULT_IDS, failed: true })
        }
      })
    return () => {
      ignore = true
    }
  }, [accountId, executionId, target, triggerGetAll])

  if (target.kind === 'clients') {
    return { clientIds: target.clientIds, resultIdByClientId: EMPTY_RESULT_IDS, isError: false }
  }
  const currentLookup = lookup?.target === target ? lookup : null
  return {
    clientIds: currentLookup?.clientIds ?? null,
    resultIdByClientId: currentLookup?.resultIdByClientId ?? EMPTY_RESULT_IDS,
    isError: currentLookup?.failed ?? false,
  }
}
